import LiveTracking from "modules/LiveTracking";
import React, { useEffect } from "react";
import {
    BrowserRouter as Router,
    Route,
    Redirect,
    useLocation,
    withRouter,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import ProjectsTable from "modules/clientProjects/Table";
import SupplierPartnerProjectsTable from "modules/supplierPartnerProjects/Table";
import MainLayout from "../modules/shared/layout/main";
import FullscreenLayout from "../modules/shared/layout/fullscreen";
import Login from "../modules/auth/components/Login";
import DriversTable from "../modules/drivers/Table";
import SuppliersTable from "../modules/suppliers/Table";
import TrucksTable from "../modules/trucks/Table";
import TruckTypesTable from "../modules/truckTypes/Table";
import LocationCategoriesTable from "../modules/locationCategories/Table";
import OrganizationsTable from "../modules/organizations/Table";
import BranchesTable from "../modules/branches/Table";
import OrganizationsProfile from "../modules/organizations/Profile";
import UsersTable from "../modules/users/Table";
import LocationsTable from "../modules/locations/Table";
import AdminsTable from "../modules/admins/Table";
import organizationAssigneesTable from "../modules/organizationAssignees/Table";
import SupplierContractsTable from "../modules/supplierContracts/Table";
import ClientContractsTable from "../modules/clientContracts/Table";
import Monitoring from "../modules/monitoring";
import Trip from "../modules/trips/components/Trip";
import TripsTable from "../modules/trips/components/Table";
import CreateOrder from "../modules/orders/components/create";
import OrdersTable from "../modules/orders/components/Table";
import DevicesTable from "../modules/devices/Table";
import Home from "../modules/home";
import { accessTokenSelector, authorizedActionsSelector } from "../modules/auth/slice";
import UnAuthorized from "../modules/auth/components/UnAuthorized";
import checkAuthorization from "../modules/auth/helpers/checkAuthorization";
import SupplierReporting from "../modules/reports/SupplierReporting";
import TagsTable from "../modules/tags/Table";
import SupplierProfile from "../modules/suppliers/Profile";
import DriverProfile from "../modules/drivers/Profile";
import CreateProjectForm from "../modules/clientProjects/components/create";
import ProjectProfile from "../modules/clientProjects/components/Profile";
import analytics from "../analytics";

const wrapWithLayout = (Component, isPanelTransparent) =>
    function () {
        return (
            <MainLayout isPanelTransparent={isPanelTransparent}>
                <Component />
            </MainLayout>
        );
    };
const wrapWithFullscreenLayout = (Component) =>
    function () {
        return (
            <FullscreenLayout>
                <Component />
            </FullscreenLayout>
        );
    };
const lang =
    localStorage.getItem("locale") && localStorage.getItem("locale").replace(/^"(.*)"$/, "$1");

function App() {
    const location = useLocation();
    const isAuthenticated = !!useSelector(accessTokenSelector());
    const authorizedActions = useSelector(authorizedActionsSelector());

    const isAuthorized = (path, resource) => {
        if (path === "/home") return true;
        return checkAuthorization(authorizedActions, resource);
    };

    function PrivateRoute({ component: Component, ...rest }) {
        return (
            <Route
                {...rest}
                render={(props) =>
                    isAuthenticated ? (
                        isAuthorized(rest.path, rest.resource) ? (
                            <Component {...props} />
                        ) : (
                            <Redirect to="/unauthorized" />
                        )
                    ) : (
                        <Redirect to="/login" />
                    )
                }
            />
        );
    }

    const userID = localStorage.getItem("userId")?.replace(/"/g, "");
    const firstName = localStorage.getItem("firstName")?.replace(/"/g, "");
    const lastName = localStorage.getItem("lastName")?.replace(/"/g, "");
    const email = localStorage.getItem("email")?.replace(/"/g, "");
    const userRoles = localStorage.getItem("userRoles");

    useEffect(() => {
        lang && document.documentElement.classList.add(lang);
        lang && document.body.classList.add(lang);
    }, []);

    useEffect(() => {
        if (location?.pathname === "/home") {
            analytics.identify(userID, {
                name: `${firstName} ${lastName}`,
                Email: email,
                Rule: userRoles,
            });
        }
    }, [location?.pathname]);

    return (
        <Router>
            <Route
                exact
                path="/"
            >
                <Redirect to="/home" />
            </Route>
            <Route
                path="/login"
                component={Login}
            />
            <Route
                exact
                path="/unauthorized"
                component={UnAuthorized}
            />
            <PrivateRoute
                path="/home"
                component={wrapWithLayout(Home)}
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.suppliers" />}
                exact
                path="/database/suppliers"
                component={wrapWithLayout(SuppliersTable)}
                resource="suppliers"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.supplier-contracts" />}
                exact
                path="/database/supplierContracts"
                component={wrapWithLayout(SupplierContractsTable)}
                resource="supplierContracts"
            />

            <PrivateRoute
                exact
                path="/database/clientProjects/create"
                component={wrapWithLayout(CreateProjectForm, true)}
                resource="projects"
            />
            <PrivateRoute
                exact
                path="/clientProjects/:id"
                component={wrapWithLayout(ProjectProfile, true)}
                resource="projects"
            />
            <PrivateRoute
                exact
                path="/database/suppliers/:id"
                component={wrapWithLayout(SupplierProfile)}
                resource="suppliers"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.drivers" />}
                exact
                path="/database/drivers"
                component={wrapWithLayout(DriversTable)}
                resource="drivers"
            />
            <PrivateRoute
                exact
                path="/database/drivers/:id"
                component={wrapWithLayout(DriverProfile)}
                resource="drivers"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.trucks" />}
                exact
                path="/database/trucks"
                component={wrapWithLayout(TrucksTable)}
                resource="trucks"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.truck-types" />}
                exact
                path="/database/truckTypes"
                component={wrapWithLayout(TruckTypesTable)}
                resource="truckTypes"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.location-categories" />}
                exact
                path="/database/locationCategories"
                component={wrapWithLayout(LocationCategoriesTable)}
                resource="locationCategories"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.clients" />}
                exact
                path="/database/clients"
                component={wrapWithLayout(OrganizationsTable)}
                resource="organizations"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.branches" />}
                exact
                path="/database/branches"
                component={wrapWithLayout(BranchesTable)}
                resource="branches"
            />
            <PrivateRoute
                exact
                path="/database/clients/:id"
                component={wrapWithLayout(OrganizationsProfile)}
                resource="organizations"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="layout.client-contracts" />}
                exact
                path="/database/clientContracts"
                component={wrapWithLayout(ClientContractsTable)}
                resource="organizationContracts"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.users" />}
                exact
                path="/database/users"
                component={wrapWithLayout(UsersTable)}
                resource="users"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.projects" />}
                exact
                path="/database/projects"
                component={wrapWithLayout(ProjectsTable)}
                resource="projects"
            />
            {/* TODO, change resource : "supplier_partner_projects"*/}
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.projects" />}
                exact
                path="/database/sp-projects"
                component={wrapWithLayout(SupplierPartnerProjectsTable)}
                resource="projects"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.admins" />}
                exact
                path="/database/admins"
                component={wrapWithLayout(AdminsTable)}
                resource="adminUsers"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.OrganizationAssignees" />}
                exact
                path="/database/organizationAssignees"
                component={wrapWithLayout(organizationAssigneesTable)}
                resource="organizationAssignees"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.locations" />}
                exact
                path="/database/locations"
                component={wrapWithLayout(LocationsTable)}
                resource="locations"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="breadcrumb.order-monitoring" />}
                exact
                path="/monitoring"
                component={wrapWithFullscreenLayout(Monitoring)}
                resource="trips"
            />
            <PrivateRoute
                exact
                path="/database/trips/:id"
                component={wrapWithLayout(Trip)}
                resource="trips"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.trips" />}
                exact
                path="/database/trips"
                component={wrapWithLayout(TripsTable, true)}
                resource="trips"
            />
            <PrivateRoute
                exact
                path="/orders/create"
                component={wrapWithLayout(CreateOrder, true)}
                resource="trips"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.orders" />}
                exact
                path="/database/orders"
                component={wrapWithLayout(OrdersTable, true)}
                resource="orders"
            />

            <PrivateRoute
                breadcrumb={<FormattedMessage id="layout.liveTracking" />}
                exact
                path="/database/liveTracking"
                component={wrapWithLayout(LiveTracking, true)}
                resource="orders"
            />

            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.tags" />}
                exact
                path="/database/tags"
                component={wrapWithLayout(TagsTable, true)}
                resource="tags"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.supplier" />}
                exact
                path="/reports/supplier"
                component={wrapWithLayout(SupplierReporting, true)}
                resource="orders"
            />
            <PrivateRoute
                breadcrumb={<FormattedMessage id="drawer.database.devices" />}
                exact
                path="/database/devices"
                component={wrapWithLayout(DevicesTable, true)}
                resource="devices"
            />
        </Router>
    );
}

export default withRouter(App);
